import {
  apiGetRoomInfo,
  apiGetMemberList,
  apiRoomEndLive,
  apiRoomLevelRunningInfo,
  apiRoomLevelMedalPics
} from '@/api/room'
import type {
  roomInfoInterface,
  memberListInterface,
  chatListInterface,
  roomLevelRunningInfoInterface,
  roomLevelMedalPicsInterface
} from '@/types/apiType/roomType'
import type { userInfoInterface } from '@/types/apiType/userType'
import type { userInfoStorageMapInterface } from '@/types/viewsType/userType'
import type {
  socketRoomInfoInterface,
  guardTop1Interface,
  barrageInterface,
  rtcInfoInterface
} from '@/types/viewsType/socketType'

import { ROOM_TYPE } from '@/utils/constant'

const TIMEOUT_TIME = 14000 // 常量 定时器倒计时

export const useRoomStore = defineStore({
  id: 'room',
  state: () => ({
    roomType: ROOM_TYPE.VIDEO_LIVE as ROOM_TYPE, // 房间类型 3:音频直播房，6：视频直播房，10：视频通话房
    roomId: 0 as number, // 房间ID
    roomUid: 0 as number, // 房间用户UID
    onlineNum: 0, // 在线人数
    roomInfo: {} as roomInfoInterface, // 房间信息
    anchorInfo: {} as userInfoInterface, // 主播个人信息，通过接口获取
    getMemberListTime: 0 as number, // 上一次获取成员列表的时间
    memberList: [] as memberListInterface[], // 房间成员列表
    chatList: [] as chatListInterface[], // 会话列表
    chatGiftList: [] as chatListInterface[], // 会话礼物列表
    chatTextList: [] as chatListInterface[], // 会话文本列表
    isShowMask: true as boolean, // 是否显示遮罩层，用作视频还没加载时显示
    // 超时功能，发布超时,监听10s后是否发布成功定时器,rtc情况下才去监听
    timeOut: {
      isTimeout: 0 as number // 超时定时器Id
    },
    isShowLiveRouteLeavDialog: true as boolean, // 是否显示直播页面离开页面确认弹窗
    isStartLive: false as boolean, // 是否开始直播
    isStartTimer: false as boolean, // 是否启动计时器，计时直播功能
    socketRoomInfo: {} as socketRoomInfoInterface, // socket房间信息
    guardTop1: {} as guardTop1Interface, // 头号粉丝信息
    barrageInfo: {} as barrageInterface, // 弹幕信息，在socket接收值，然后在弹幕组件监听
    /** 直播RTC SDK配置信息 */
    rtcInfo: {} as rtcInfoInterface, // 直播RTC SDK配置信息
    /** 是否在WebView打开的页面 */
    isApp: false as boolean,
    /** 房间密码入口对话框 */
    roomPasswordDialogConfig: {
      isShow: false as boolean,
      password: '' // 密码
    },
    /** 房主member信息 */
    hostMemberInfo: {} as memberListInterface,
    /** 用户member信息 */
    userMemberInfo: {} as memberListInterface,
    /**退出房间类型, 0:显示RoomSheet，1：直接退出，2：最小化退出，3：通话房间退出 */
    quitRoomType: 0 as 0 | 1 | 2 | 3,
    /** 房间等级信息 语聊房会用到 */
    roomLevelInfo: {} as roomLevelRunningInfoInterface,
    /** 房间等级勋章图片集合 */
    roomLevelMedalPics: [] as roomLevelMedalPicsInterface[]
  }),
  getters: {
    // 主播信息，优先从公共缓存数据从获取
    anchorInfoStorage: (state): userInfoStorageMapInterface => {
      const userStore = useUserStore()
      return userStore.userInfoStorageMap[state.roomUid] || state.anchorInfo
    },
    /** 是否房主直播*/
    isHostLive: (state) => {
      const userStore = useUserStore()
      return (
        state.roomUid === userStore.userInfo.uid &&
        state.roomUid !== 0 &&
        userStore.userInfo.uid !== 0
      )
    }
  },
  actions: {
    /** 获取房间等级勋章图片集合 */
    getRoomLevelMedalPics() {
      //确保只获取一次
      if (this.roomLevelMedalPics.length > 0 && !this.isApp) return
      apiRoomLevelMedalPics().then((res) => {
        if (res.code === 200) {
          this.roomLevelMedalPics = res.data
        }
      })
    },
    /** 获取房间等级信息  */
    getRoomLevelInfo() {
      if (!this.roomId) return
      apiRoomLevelRunningInfo({ roomId: this.roomId }).then((res) => {
        if (res.code === 200) {
          this.roomLevelInfo = res.data
        }
      })
    },
    /**
     * 设置显示房间显示房间Sheet
     * @param {0 | 1 | 2 | 3} value 0:显示RoomSheet，1：直接退出，2：最小化退出，3：通话房间退出
     **/
    setQuitRoomType(value: 0 | 1 | 2 | 3) {
      this.quitRoomType = value
    },
    /**
     * 设置用户member信息
     */
    setUserMemberInfoKeyValue(key: string, value: number | boolean) {
      this.userMemberInfo[key] = value
    },
    /** App渲染初始化房间uid */
    setRoomUid(roomUid: number) {
      this.roomUid = roomUid
    },
    /** 服务端渲染初始化房间类型 */
    setRoomType(type: ROOM_TYPE) {
      this.roomType = type
    },
    /**
     * 设置显示房间密码入口对话框
     */
    setRoomPasswordDialogConfig(config: {
      isShow: boolean
      password?: string
    }) {
      this.roomPasswordDialogConfig.isShow = config.isShow
      if (config.password || config.password === '') {
        this.roomPasswordDialogConfig.password = config.password
      }
    },
    /**
     * 设置是否在IOSWebView打开的页面
     **/
    setIsApp(value: boolean) {
      this.isApp = value
    },
    /** 设置rtcInfo */
    setRtcInfo(rtcInfo: rtcInfoInterface) {
      this.rtcInfo = rtcInfo
    },
    /** 设置头号粉丝信息*/
    setGuardTop1Info(value: guardTop1Interface) {
      this.guardTop1 = value
    },
    /** 设置弹幕信息*/
    setBarrageInfo(value: barrageInterface) {
      this.barrageInfo = value
    },
    /** 去主播直播结束页面*/
    toHostLiveEndPage() {
      const router = useRouter()
      if (this.isHostLive) {
        apiRoomEndLive({ roomId: this.roomId }).then((res) => {
          if (res.code === 200) {
            this.setIsShowLiveRouteLeavDialog(false)
            router.replace({ path: '/live/LiveEnd', query: { ...res.data } })
          }
        })
      }
    },
    /**
     * 设置socket房间信息金豆
     */
    setSocketRoomInfoKeyValue(key: string, value: number) {
      this.socketRoomInfo[key] = value
    },
    /**
     * 设置socket房间信息
     */
    setSocketRoomInfo(type: 'init' | 'update', value) {
      const audioStore = useAudioStore()
      this.socketRoomInfo = value
      // 如果更新情况下关闭公屏初始化公屏消息
      if (type === 'update' && this.socketRoomInfo.publicChatSwitch === 1) {
        this.updateChatList('init')
      }

      audioStore.setSeats(this.socketRoomInfo.seats || 10)
    },
    /**
     * 是否启动计时器
     * @param {boolean} value
     */
    setIsStartTimer(value: boolean) {
      this.isStartTimer = value
    },
    /**
     * 判断主播是否在直播
     * @param {Array<number | string>} ids 远程用户
     */
    whetherHostIsLive(ids: Array<number | string>) {
      // 用临时数据保存是为了区分开播，还是由未开播变成开始直播
      const temporaryIsLive = ids.some((id) => {
        return id == this.roomUid
      })

      // 房主由未开播变成开始直播
      if (!this.isStartLive && temporaryIsLive) {
        this.onStartLive()
      }
      this.isStartLive = temporaryIsLive
    },
    /**
     * 设置主播是否在直播
     * @param {boolean} value 房间ID
     */
    setIsHostLive(value: boolean) {
      this.isStartLive = value
      // 如果房主开始直播或复播
      if (this.isStartLive) {
        this.onStartLive()
      }
    },
    /**
     * 去直播间，因需要检测是否支持SDK，故去直播间一定要调此API
     * @param {string | number} roomId 房间ID
     * @param {string | number} roomUid 房主UID
     */
    toLiveRoom(query: {
      roomUid: string | number
      roomId?: string | number
      roomType: ROOM_TYPE
    }) {
      const router = useRouter()
      const audioStore = useAudioStore()

      // 重置扬声器
      audioStore.resetData()
      audioStore.setIsOpenSound(true)

      if (query.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
        router.push({
          path: '/room/audio',
          query: {
            roomUid: common.encryptParam(String(query.roomUid)),
            roomId: common.encryptParam(String(query.roomId))
          }
        })
      } else {
        router.push({
          path: '/room/live',
          query: {
            roomUid: common.encryptParam(String(query.roomUid)),
            roomId: common.encryptParam(String(query.roomId))
          }
        })
      }
    },
    /**
     * 主播开始直播监听 开播和复播
     * */
    onStartLive() {
      const i18n = useNuxtApp().$i18n
      if (this.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
        const roomStore = useRoomStore()
        // 如果是视频通话 监听到远程用户就向公屏发送 等待消息
        roomStore.updateChatList('put', {
          content: i18n.t('room.joinWaitingChatTips'),
          type: constant.ROOM_CHAT_TYPE.PROMPT_TEXT
        })
      }
    },
    /**
     * 设置是否显示直播页面离开页面确认弹窗
     * @param {boolean} value 开关
     */
    setIsShowLiveRouteLeavDialog(value: boolean) {
      this.isShowLiveRouteLeavDialog = value
    },
    // 设置超时定时器
    setIsTimeout() {
      const i18n = useNuxtApp().$i18n
      if (
        this.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
        !this.timeOut.isTimeout
      ) {
        this.timeOut.isTimeout = window.setTimeout(() => {
          const agoraStore = useAgoraStore()
          const trtcStore = useTrtcStore()
          const isPublish =
            this.rtcInfo.rtcType === 'AGORA'
              ? agoraStore.isVideoPublish
              : trtcStore.isVideoPublish
          const isPirstFrame =
            this.rtcInfo.rtcType === 'AGORA'
              ? agoraStore.isRemoteUserPirstFrame
              : trtcStore.isRemoteUserPirstFrame
          showToast(i18n.t('call.connectionFailed'))
          console.log(
            'AgoraSDK => 超时退出',
            `发布：${isPublish}，首帧：${isPirstFrame}`
          )
          const callStore = useCallStore()
          callStore.onCallClose('')
          // 如果是在APP超时，就调强制退出房间
          if (this.isApp) {
            AppNative.appFun(AppNative.iosNeedMessageFun.exitChatRoom)
          }
        }, TIMEOUT_TIME)
      }
    },
    // 关闭超时定时器
    closeIsTimeout() {
      const agoraStore = useAgoraStore()
      const trtcStore = useTrtcStore()
      const isPublish =
        this.rtcInfo.rtcType === 'AGORA'
          ? agoraStore.isVideoPublish
          : trtcStore.isVideoPublish
      const isPirstFrame =
        this.rtcInfo.rtcType === 'AGORA'
          ? agoraStore.isRemoteUserPirstFrame
          : trtcStore.isRemoteUserPirstFrame
      if (
        this.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
        this.timeOut.isTimeout &&
        isPublish &&
        isPirstFrame
      ) {
        console.log('AgoraSDK => 关闭超时退出')
        this.timeOut.isTimeout && window.clearTimeout(this.timeOut.isTimeout)
      }
    },
    // 设置遮罩层
    setIsShowMask(show: boolean) {
      this.isShowMask = show
    },
    /**
     * 更新会话列表
     * @param {string} type init:初始化,put
     * */
    updateChatList(type: string, obj?: chatListInterface) {
      const roomStore = useRoomStore()
      const userStore = useUserStore()
      switch (type) {
        case 'init':
          this.chatList = []
          this.chatTextList = []
          this.chatGiftList = []
          break
        case 'put':
          if (obj?.type === constant.ROOM_CHAT_TYPE.CHAT_TEXT) {
            // 会话文本
            this.chatTextList.push(obj as chatListInterface)
            this.chatList.push(obj as chatListInterface)
          } else if (obj?.type === constant.ROOM_CHAT_TYPE.CHAT_GIFT) {
            // 会话礼物
            if (roomStore.socketRoomInfo.isBlockGiftMsg === 1) {
              this.chatGiftList.push(obj as chatListInterface)
              this.chatList.push(obj as chatListInterface)
            }
          } else if (obj?.type === constant.ROOM_CHAT_TYPE.GAME) {
            // 游戏返利如果是app需要判断条件
            if (
              !roomStore.isApp ||
              (roomStore.isApp &&
                (userStore.userInfo.videoRoomExperLevel >= 5 ||
                  userStore.userInfo.gid))
            ) {
              this.chatList.push(obj as chatListInterface)
            }
          } else {
            // 其他消息
            this.chatList.push(obj as chatListInterface)
          }
          break
        default:
          break
      }
    },
    // 初始化房间
    async initRoom(info: {
      roomType: ROOM_TYPE
      roomUid: number
      roomId?: number
    }) {
      const rechargeStore = useRechargeStore()

      this.roomType = info.roomType
      this.roomId = info.roomId || 0
      this.roomUid = info.roomUid
      this.getMemberListTime = 0
      this.onlineNum = 0
      console.log(
        '房间初始化 => 初始化',
        `roomType:${this.roomType},roomId:${this.roomId},roomUid:${this.roomUid}`
      )
      // 进入直播页面同时重置 isShowLiveRouteLeavDialog
      this.setIsShowLiveRouteLeavDialog(true)
      // 不是主播的就获取主播信息
      if (!this.isHostLive) {
        this.getAnchorInfo()
      }

      if (this.roomId) {
        this.getRoomInfo(this.roomId)
      } // socket进房
      this.socketEnterRoom()

      if (this.isApp) {
        rechargeStore.getAppFirstChargeInfo()
      }

      // 语音房获取的数据
      if (this.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
        const micEmojiStore = useMicEmojiStore()
        micEmojiStore.initMicEmojiInfo()
        this.getRoomLevelMedalPics()
      }
      // 视频房，语音房获取数据
      if (
        this.roomType === constant.ROOM_TYPE.AUDIO_LIVE ||
        this.roomType === constant.ROOM_TYPE.VIDEO_LIVE
      ) {
        const rocketStore = useRocketStore()
        rocketStore.getConfig()
      }
    },
    /**
     * socket进入直播状态的房间
     */
    async socketEnterRoom() {
      const socketStore = useSocketStore()
      const hostLiveStore = useHostLiveStore()
      const router = useRouter()

      // live进房
      if (
        this.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        this.roomType === constant.ROOM_TYPE.AUDIO_LIVE
      ) {
        console.log('房间初始化 => 视频或语音直播进房')
        // 主播进房
        if (this.isHostLive) {
          // 视频主播进房,是就判断是否有资格开播,APP就直接算成功
          if (hostLiveStore.isRoomFrontCheckSuccess || this.isApp) {
            // 不是App才需要去 hostLiveStore.roomPrepareCheckConfig 获取查询
            const roomInfoConfig = !this.isApp
              ? hostLiveStore.roomPrepareCheckConfig.roomInfo.find(
                  (v) => v.roomType === constant.ROOM_TYPE.VIDEO_LIVE
                )
              : { coverUrl: '' }
            socketStore.snedEnterWithOpenChatRoom({
              uid: this.roomUid,
              type: this.roomType,
              title: hostLiveStore.roomFrontCheckForm.startLiveTitle,
              avatar:
                roomInfoConfig?.coverUrl ||
                hostLiveStore.roomFrontCheckForm.avatar
            })
          } else {
            // 没资格就需要重新验证流程
            await hostLiveStore.getRoomPrepareCheckConfig(true)
            hostLiveStore
              .roomFrontCheck(this.roomType)
              .then(() => {
                socketStore.snedEnterWithOpenChatRoom({
                  uid: this.roomUid,
                  type: this.roomType as 3 | 6
                })
              })
              .catch(() => {
                this.setIsShowLiveRouteLeavDialog(false)
                const runtimeConfig = useRuntimeConfig()
                // 是否重定向首页，不在APP内，当前域名要和 cdnUrl 一样才可以重定向
                if (
                  !this.isApp &&
                  (location.origin === runtimeConfig.public.cdnUrl ||
                    runtimeConfig.public.appMode === 'development')
                ) {
                  router.replace('/')
                }
              })
          }
        } else {
          // 非主播进房
          socketStore.snedEnterWithOpenChatRoom({
            uid: this.roomUid,
            type: this.roomType,
            pwd: this.roomPasswordDialogConfig.password,
            avatar: this.anchorInfoStorage.avatar,
            title: this.roomInfo.title
          })
        }
      }
      // 视频通话房
      if (this.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
        console.log('房间初始化 => 视频通话进房')
        socketStore.snedEnterWithOpenChatRoom({
          uid: this.roomUid,
          type: 10
        })
        if (this.isApp) {
          const callStore = useCallStore()
          callStore.getCallPrices(1)
        }
      }
      // 短视频通话房
      if (this.roomType === constant.ROOM_TYPE.VIDEO_CALL_SHORT) {
        const callStore = useCallStore()
        const userStore = useUserStore()
        const socketStore = useSocketStore()
        // 开始倒计时
        nextTick(() => {
          callStore.onStartBilling()
          // this.setIsShowMask(false)
        })
        socketStore.setIsEnterRoom(true)
        this.memberList = [this.anchorInfoStorage, userStore.userInfo]
        this.onlineNum = 2
      }
    },
    /**socket进房成功回调*/
    onEnterRoomSuccess(data) {
      const callStore = useCallStore()
      const userState = useUserStore()
      this.hostMemberInfo = data.room_user
      this.userMemberInfo = data.member

      // 到这一步，this.roomId 没值就获取房间信息，有了就说明获取过了
      if (!this.roomId) {
        this.roomId = data.room_info.roomId
        // 获取房间信息
        this.getRoomInfo(this.roomId)
      }
      this.roomId = data.room_info.roomId

      if (this.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
        this.getRoomLevelInfo()
      }

      // 设置房间相关信息
      this.setSocketRoomInfo('init', data.room_info)
      this.setRtcInfo(data.rtcInfo)

      // 进入房间提示
      if (data.room_info.playInfo) {
        this.updateChatList('put', {
          content: data.room_info.playInfo,
          type: constant.ROOM_CHAT_TYPE.PROMPT_TEXT
        })
      }

      // 是否探房间公告弹窗
      if (data.room_info.popNotice) {
        showConfirmDialog({
          title: data.room_info.roomDesc,
          message: `Room Title: ${data.room_info.title}\n${data.room_info.roomNotice}`,
          showCancelButton: false,
          showConfirmButton: false,
          closeOnClickOverlay: true,
          className: 'private-dialog-customize single-btn text-left'
        })
          .then(() => {})
          .catch(() => {})
      }

      // 判断房主是否在直播
      const isLive = data.queue_list.some((v) => {
        return v.value.member && v.value.member.uid === this.roomUid
      })
      this.setIsHostLive(isLive)

      // 设置头号粉丝
      if (data.guardTop1) {
        this.setGuardTop1Info(JSON.parse(data.guardTop1))
      }

      // 成功隔壁输入密码弹窗
      this.setRoomPasswordDialogConfig({ isShow: false })

      // 判断是否是1v1，埋点
      if (this.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
        // 如果是视频通话
        if (callStore.callType === 1) {
          eventTracking.eventLog(eventTracking.eventId.enter_private_room) // 进入1v1视频通话房间成功
          if (userState.isNewUser) {
            eventTracking.eventLog(
              eventTracking.eventId.enter_private_room_new_user
            )
          }
        } else {
          // 如果是匹配通话
          eventTracking.eventLog(eventTracking.eventId.enter_match_room) // 进入匹配视频通话房间成功
          if (userState.isNewUser) {
            eventTracking.eventLog(
              eventTracking.eventId.enter_match_room_new_user
            )
          }
        }
      } else {
        // 是直播间
        eventTracking.eventLog(eventTracking.eventId.enter_room) // 进入直播房间成功
      }
    },
    /** 接口获取房间信息 */
    getRoomInfo(roomId: number) {
      // 获取房间相关信息
      apiGetRoomInfo({ roomId }).then((res) => {
        const { code, data } = res
        if (code === 200) {
          this.roomInfo = data
        }
      })
      this.getMemberList()
    },
    // 获取主播信息
    // 如果有uid过来的，就优先拿uid,目前有uid过来的是拨打或匹配
    async getAnchorInfo(uid?: number) {
      const userStore = useUserStore()
      if (!(uid || this.roomUid)) return
      userStore.publicGetUserInfo(uid || this.roomUid).then((res) => {
        this.anchorInfo = res
        if (uid) {
          console.log('开始预加载头像')
          // 如果需要头像预加载，为封面做准备
          new Image().src = this.anchorInfo.avatar as string
        }
      })
    },
    // 获取房间成员
    async getMemberList(force?: boolean) {
      if (!this.roomId) {
        return
      }
      // 直播房上次和下次获取时间相差10秒才获取，RTC模式直接刷新
      const nowTime = new Date().getTime()
      if (
        nowTime - this.getMemberListTime >= 10000 ||
        this.roomType === constant.ROOM_TYPE.VIDEO_CALL ||
        force
      ) {
        this.getMemberListTime = nowTime
        const res = await apiGetMemberList({
          room_id: this.roomId,
          page: 1,
          pageSize: 20
        })
        const { code, data } = res
        if (code === 200) {
          this.memberList = [...data.vip_member_list, ...data.member_list]
          const userStore = useUserStore()
          userStore.updateUserInfoStorageMap({
            type: 'put',
            list: [...this.memberList]
          })
          this.onlineNum = this.memberList.length
        }
      }
    },
    /**
     * 被踢出房间
     */
    kickedOutRoom() {
      // 把确认退出确认框先关闭
      const callStore = useCallStore()
      callStore.setIsShowCallRouteLeavDialog(false)
      this.setIsShowLiveRouteLeavDialog(false)
      // 被踢出房间,重置到首页
      const router = useRouter()
      router.replace('/')
    },
    // 退出
    quit() {
      if (this.roomId && this.roomUid) {
        if (this.roomType !== constant.ROOM_TYPE.VIDEO_CALL_SHORT) {
          const socketStore = useSocketStore()
          socketStore.sendExitChatRoom()
        }
        this.resetData()
      }
    },
    // 重置数据
    resetData() {
      const animationStore = useAnimationStore()
      const micEmojiStore = useMicEmojiStore()
      const micApplyStore = useMicApplyStore()
      const rechargeStore = useRechargeStore()
      animationStore.resetData()
      micEmojiStore.resetData()
      micApplyStore.resetData()

      this.isShowMask = true
      // 清除定时器
      this.timeOut.isTimeout && window.clearTimeout(this.timeOut.isTimeout)
      this.timeOut.isTimeout = 0
      this.isStartTimer = false
      this.roomId = 0
      this.roomUid = 0
      this.roomInfo = defaultData.defaultRoomInfo()
      this.updateChatList('init')
      this.memberList = []
      this.onlineNum = 0
      this.anchorInfo = defaultData.defaultUserInfo()
      this.rtcInfo = defaultData.defaultRtcInfo()
      this.roomPasswordDialogConfig.password = ''
      this.setSocketRoomInfo('update', {})
      this.setGuardTop1Info({ uid: 0, erbanNo: 0, nick: '', avatar: '' })
      this.setBarrageInfo({ avatar: '', nick: '', barrageMsg: '', id: 0 })
      this.hostMemberInfo = {} as memberListInterface
      this.userMemberInfo = {} as memberListInterface
      this.roomLevelInfo = {} as roomLevelRunningInfoInterface
      rechargeStore.setAppFirstCjargeTime(0)
    }
  },
  persist: {
    key: 'roomStore',
    paths: ['roomInfo', 'anchorInfo']
  }
})
